import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STATUS_ORDER, STATUS_META } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/Badges";
import { PhoneCall, MessageCircle, ClipboardList, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  location: "",
  project: "",
  status: "new",
  temperature: "cold",
  budget: "",
  requirement: "",
  source: "",
  assigned_to: "",
  note: "",
};

export default function LeadDialog({ open, onOpenChange, lead, users, onDeleted }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(EMPTY);
  const [callDuration, setCallDuration] = useState("");
  const [activityNote, setActivityNote] = useState("");
  const isEdit = Boolean(lead?.id);

  useEffect(() => {
    if (lead) setForm({ ...EMPTY, ...lead, assigned_to: lead.assigned_to || "" });
    else setForm(EMPTY);
    setCallDuration("");
    setActivityNote("");
  }, [lead, open]);

  const activitiesQ = useQuery({
    queryKey: ["activities", lead?.id],
    queryFn: async () => (await api.get(`/leads/${lead.id}/activities`)).data.items,
    enabled: Boolean(lead?.id) && open,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        assigned_to: form.assigned_to || null,
      };
      if (!payload.email) delete payload.email;
      if (isEdit) {
        const { data } = await api.patch(`/leads/${lead.id}`, payload);
        return data;
      }
      const { data } = await api.post("/leads", payload);
      return data;
    },
    onSuccess: () => {
      toast.success(isEdit ? "Lead updated" : "Lead created");
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["locations"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const activityMut = useMutation({
    mutationFn: async (payload) => (await api.post(`/leads/${lead.id}/activities`, payload)).data,
    onSuccess: () => {
      toast.success("Activity logged");
      qc.invalidateQueries({ queryKey: ["activities", lead.id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
      setActivityNote("");
      setCallDuration("");
    },
    onError: () => toast.error("Failed to log activity"),
  });

  const deleteMut = useMutation({
    mutationFn: async () => (await api.delete(`/leads/${lead.id}`)).data,
    onSuccess: () => {
      toast.success("Lead deleted");
      qc.invalidateQueries({ queryKey: ["leads"] });
      onDeleted?.();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl overflow-y-auto max-h-[92vh]" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Lead details" : "Add a new lead"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update contact info, status and log activities against this lead."
              : "Capture a new prospect for your pipeline."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-2">
          <section className="space-y-3">
            <Field label="Name">
              <Input
                data-testid="lead-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
              />
            </Field>
            <Field label="Phone">
              <Input
                data-testid="lead-form-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="10-digit number"
              />
            </Field>
            <Field label="Email">
              <Input
                data-testid="lead-form-email"
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Optional"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Location">
                <Input
                  data-testid="lead-form-location"
                  value={form.location || ""}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Baner, Wakad…"
                />
              </Field>
              <Field label="Project">
                <Input
                  data-testid="lead-form-project"
                  value={form.project || ""}
                  onChange={(e) => setForm({ ...form, project: e.target.value })}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Budget">
                <Input
                  data-testid="lead-form-budget"
                  value={form.budget || ""}
                  onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  placeholder="e.g. 1Cr–1.5Cr"
                />
              </Field>
              <Field label="Requirement">
                <Input
                  data-testid="lead-form-requirement"
                  value={form.requirement || ""}
                  onChange={(e) => setForm({ ...form, requirement: e.target.value })}
                  placeholder="2 BHK, Villa…"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status">
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger data-testid="lead-form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s}>
                        <span className="flex items-center">
                          <span className={`status-dot status-${s}`} /> {STATUS_META[s].label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Temperature">
                <Select
                  value={form.temperature}
                  onValueChange={(v) => setForm({ ...form, temperature: v })}
                >
                  <SelectTrigger data-testid="lead-form-temp">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cold">Cold</SelectItem>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="hot">Hot</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {isAdmin ? (
                <Field label="Assigned to">
                  <Select
                    value={form.assigned_to || "__none"}
                    onValueChange={(v) =>
                      setForm({ ...form, assigned_to: v === "__none" ? "" : v })
                    }
                  >
                    <SelectTrigger data-testid="lead-form-assignee">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Unassigned</SelectItem>
                      {(users || [])
                        .filter((u) => !u.is_off || u.id === form.assigned_to)
                        .map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name}
                            {u.is_off ? " (Off)" : ""}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </Field>
              ) : (
                <Field label="Assigned to">
                  <Input value={lead?.assigned_to_name || user?.name || "You"} disabled data-testid="lead-form-assignee-readonly" />
                </Field>
              )}
              <Field label="Source">
                <Input
                  data-testid="lead-form-source"
                  value={form.source || ""}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                  placeholder="Facebook, Referral…"
                />
              </Field>
            </div>
            <Field label="Note">
              <Textarea
                data-testid="lead-form-note"
                rows={3}
                value={form.note || ""}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </Field>
          </section>

          {/* Activity panel */}
          <section className="space-y-3">
            <div className="soft-card p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-navy">
                <ClipboardList className="h-4 w-4" /> Log an activity
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  data-testid="activity-duration"
                  placeholder="Call duration (sec)"
                  type="number"
                  value={callDuration}
                  onChange={(e) => setCallDuration(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    data-testid="log-call-btn"
                    variant="outline"
                    className="flex-1"
                    disabled={!isEdit || activityMut.isPending}
                    onClick={() =>
                      activityMut.mutate({
                        type: "call",
                        duration_seconds: Number(callDuration || 0),
                        note: activityNote || null,
                      })
                    }
                  >
                    <PhoneCall className="h-4 w-4" /> Call
                  </Button>
                  <Button
                    data-testid="log-wa-btn"
                    variant="outline"
                    className="flex-1"
                    disabled={!isEdit || activityMut.isPending}
                    onClick={() =>
                      activityMut.mutate({
                        type: "whatsapp",
                        note: activityNote || null,
                      })
                    }
                  >
                    <MessageCircle className="h-4 w-4" /> WA
                  </Button>
                </div>
              </div>
              <Textarea
                data-testid="activity-note"
                className="mt-2"
                rows={2}
                placeholder="Optional note"
                value={activityNote}
                onChange={(e) => setActivityNote(e.target.value)}
              />
              <Button
                data-testid="log-note-btn"
                variant="secondary"
                className="mt-2 w-full"
                disabled={!isEdit || !activityNote || activityMut.isPending}
                onClick={() =>
                  activityMut.mutate({ type: "note", note: activityNote })
                }
              >
                Save note only
              </Button>
              {!isEdit && (
                <p className="mt-2 text-xs text-slate-400">Save the lead first to log activities.</p>
              )}
            </div>

            <div className="soft-card p-4">
              <div className="mb-3 text-sm font-semibold text-navy">Timeline</div>
              {isEdit ? (
                <ul className="max-h-56 space-y-2 overflow-y-auto pr-1 text-sm">
                  {(activitiesQ.data || []).length === 0 && (
                    <li className="text-xs text-slate-400">No activity yet.</li>
                  )}
                  {(activitiesQ.data || []).map((a) => (
                    <li key={a.id} className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span className="font-semibold uppercase tracking-wide">{a.type.replace("_", " ")}</span>
                        <time>{new Date(a.created_at).toLocaleString()}</time>
                      </div>
                      <div className="mt-1 text-[13px] text-slate-700">
                        {a.note || (a.status_to ? `Status → ${STATUS_META[a.status_to]?.label || a.status_to}` : "—")}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        by {a.user_name}
                        {a.duration_seconds ? ` · ${a.duration_seconds}s` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-400">Timeline appears after you save the lead.</p>
              )}
            </div>
          </section>
        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <div>
            {isEdit && isAdmin && (
              <Button
                data-testid="lead-delete-btn"
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => {
                  if (window.confirm(`Delete lead ${lead.name}? This cannot be undone.`)) {
                    deleteMut.mutate();
                  }
                }}
              >
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lead-cancel-btn">
              Cancel
            </Button>
            <Button
              data-testid="lead-save-btn"
              className="bg-navy hover:brightness-110"
              disabled={saveMut.isPending}
              onClick={() => saveMut.mutate()}
            >
              {isEdit ? "Save changes" : "Create lead"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-1 block text-xs font-medium text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
